import { BaseApiClient } from "../api/baseApiClient.service";
import type { PlanLimits } from "../auth/auth.types";
import type {
	UsageStats,
	LimitCheckResult,
	CheckLimitParams,
} from "./subscription.types";
import type { SubscriptionPlan } from "../platformManager/platformManager.service";

/**
 * Service for managing subscription limits and usage
 */
export class SubscriptionService {
	private static baseApi = BaseApiClient.getInstance();

	/**
	 * Get current usage statistics for the authenticated user
	 */
	static async getUserUsage(): Promise<UsageStats> {
		try {
			const response = await SubscriptionService.baseApi.get<UsageStats>(
				"subscription/user/usage",
			);
			return response;
		} catch (error) {
			console.error("Failed to fetch user usage:", error);
			// Return zeros if fetch fails
			return {
				chats: 0,
				storageEpisodes: 0,
				workspaces: 0,
			};
		}
	}

	/**
	 * Check if a user can perform an action based on their current usage and limits
	 */
	static checkLimit(params: CheckLimitParams): LimitCheckResult {
		const { limitKey, currentCount, limits } = params;
		const limitValue = limits[limitKey];

		// -1 means unlimited
		if (limitValue === -1) {
			return {
				allowed: true,
				remaining: "unlimited",
				isAtLimit: false,
				isNearLimit: false,
				maxAllowed: "unlimited",
				current: currentCount,
			};
		}

		const remaining = limitValue - currentCount;
		const isAtLimit = remaining <= 0;
		const isNearLimit = remaining <= limitValue * 0.1; // 10% or less remaining

		return {
			allowed: !isAtLimit,
			remaining: Math.max(0, remaining),
			isAtLimit,
			isNearLimit: !isAtLimit && isNearLimit,
			maxAllowed: limitValue,
			current: currentCount,
		};
	}

	/**
	 * Check if user can create a new chat
	 */
	static canCreateChat(
		currentCount: number,
		limits: PlanLimits,
	): LimitCheckResult {
		return SubscriptionService.checkLimit({
			limitKey: "maxChats",
			currentCount,
			limits,
		});
	}

	/**
	 * Check if user can create a new storage episode
	 */
	static canStoreEpisode(
		currentCount: number,
		limits: PlanLimits,
	): LimitCheckResult {
		return SubscriptionService.checkLimit({
			limitKey: "maxStorageEpisodes",
			currentCount,
			limits,
		});
	}

	/**
	 * Check if user has access to a specific feature
	 */
	static hasFeature(
		featureKey: keyof PlanLimits["features"],
		limits: PlanLimits,
	): boolean {
		return limits.features[featureKey] ?? false;
	}

	/**
	 * Format limit value for display (handles unlimited case)
	 */
	static formatLimit(value: number): string {
		return value === -1 ? "∞" : value.toString();
	}

	/**
	 * Get upgrade URL or message
	 */
	static getUpgradeUrl(): string {
		return "/pricing";
	}

	/**
	 * Get all subscription plans (public endpoint, no auth required)
	 * Used by pricing page to display available plans
	 */
	static async getPlans(): Promise<SubscriptionPlan[]> {
		try {
			const response = await SubscriptionService.baseApi.get<{
				success: boolean;
				plans: SubscriptionPlan[];
			}>("subscription/plans");

			if (!response.success) {
				throw new Error("Failed to fetch subscription plans");
			}

			return response.plans;
		} catch (error) {
			console.error("Failed to fetch subscription plans:", error);
			throw error;
		}
	}

	/**
	 * Get user's invoices
	 */
	static async getInvoices(): Promise<{
		success: boolean;
		invoices: Array<{
			id: string;
			number: string | null;
			amount: number;
			currency: string;
			status: string | null;
			created: number;
			dueDate: number | null;
			paidAt: number | null;
			hostedInvoiceUrl: string | null;
			invoicePdf: string | null;
			description: string | null;
			periodStart: number;
			periodEnd: number;
		}>;
	}> {
		try {
			const response = await SubscriptionService.baseApi.get<{
				success: boolean;
				invoices: Array<{
					id: string;
					number: string | null;
					amount: number;
					currency: string;
					status: string | null;
					created: number;
					dueDate: number | null;
					paidAt: number | null;
					hostedInvoiceUrl: string | null;
					invoicePdf: string | null;
					description: string | null;
					periodStart: number;
					periodEnd: number;
				}>;
			}>("stripe/invoices");

			return response;
		} catch (error) {
			console.error("Failed to fetch invoices:", error);
			return {
				success: false,
				invoices: [],
			};
		}
	}

	/**
	 * Get subscription details including cancellation and downgrade status
	 */
	static async getSubscriptionDetails(): Promise<{
		success: boolean;
		hasSubscription: boolean;
		cancelAtPeriodEnd: boolean;
		currentPeriodEnd: Date | null;
		status?: string;
		hasScheduledDowngrade: boolean;
		scheduledPlanName: string | null;
	}> {
		try {
			const response = await SubscriptionService.baseApi.get<{
				success: boolean;
				hasSubscription: boolean;
				cancelAtPeriodEnd: boolean;
				currentPeriodEnd: string | null;
				status?: string;
				hasScheduledDowngrade: boolean;
				scheduledPlanName: string | null;
			}>("stripe/subscription-details");

			return {
				...response,
				currentPeriodEnd: response.currentPeriodEnd
					? new Date(response.currentPeriodEnd)
					: null,
			};
		} catch (error) {
			console.error("Failed to get subscription details:", error);
			return {
				success: false,
				hasSubscription: false,
				cancelAtPeriodEnd: false,
				currentPeriodEnd: null,
				hasScheduledDowngrade: false,
				scheduledPlanName: null,
			};
		}
	}

	/**
	 * Cancel user's subscription (at period end)
	 */
	static async cancelSubscription(): Promise<{
		success: boolean;
		message: string;
		cancelAtPeriodEnd: boolean;
		currentPeriodEnd: Date | null;
	}> {
		try {
			const response = await SubscriptionService.baseApi.post<
				{
					success: boolean;
					message: string;
					cancelAtPeriodEnd: boolean;
					currentPeriodEnd: string | null;
				},
				Record<string, never>
			>("stripe/cancel-subscription", {});

			return {
				...response,
				currentPeriodEnd: response.currentPeriodEnd
					? new Date(response.currentPeriodEnd)
					: null,
			};
		} catch (error) {
			console.error("Failed to cancel subscription:", error);
			throw error;
		}
	}

	/**
	 * Reactivate a cancelled subscription
	 */
	static async reactivateSubscription(): Promise<{
		success: boolean;
		message: string;
	}> {
		try {
			const response = await SubscriptionService.baseApi.post<
				{
					success: boolean;
					message: string;
				},
				Record<string, never>
			>("stripe/reactivate-subscription", {});

			return response;
		} catch (error) {
			console.error("Failed to reactivate subscription:", error);
			throw error;
		}
	}

	/**
	 * Upgrade subscription to a higher tier
	 */
	static async upgradeSubscription(newPriceId: string): Promise<{
		success: boolean;
		message: string;
	}> {
		try {
			const response = await SubscriptionService.baseApi.post<
				{
					success: boolean;
					message: string;
				},
				{ newPriceId: string }
			>("stripe/upgrade-subscription", { newPriceId });

			return response;
		} catch (error) {
			console.error("Failed to upgrade subscription:", error);
			throw error;
		}
	}

	/**
	 * Downgrade subscription to a lower tier
	 */
	static async downgradeSubscription(newPriceId: string): Promise<{
		success: boolean;
		message: string;
	}> {
		try {
			const response = await SubscriptionService.baseApi.post<
				{
					success: boolean;
					message: string;
				},
				{ newPriceId: string }
			>("stripe/downgrade-subscription", { newPriceId });

			return response;
		} catch (error) {
			console.error("Failed to downgrade subscription:", error);
			throw error;
		}
	}
}
